import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// Auth
export const authService = {
  login: (email, password) =>
    axios.post(`${API_BASE_URL}/auth/login`, { email, password }),
  register: (firstName, lastName, email, password, organizationName, selectedPlan, selectedModules) =>
    axios.post(`${API_BASE_URL}/auth/register`, {
      firstName,
      lastName,
      email,
      password,
      organizationName,
      selectedPlan,
      selectedModules,
    }),
  getCurrentUser: () =>
    axios.get(`${API_BASE_URL}/auth/me`, { headers: getAuthHeader() }),
  forgotPassword: (email) =>
    axios.post(`${API_BASE_URL}/auth/forgot-password`, { email }),
  resetPassword: (token, newPassword) =>
    axios.post(`${API_BASE_URL}/auth/reset-password`, { token, newPassword }),
  verifyEmail: (token) =>
    axios.post(`${API_BASE_URL}/auth/verify-email`, { token }),
  resendVerification: () =>
    axios.post(`${API_BASE_URL}/auth/resend-verification`, {}, { headers: getAuthHeader() }),
  changePassword: (currentPassword, newPassword) =>
    axios.put(`${API_BASE_URL}/auth/change-password`, { currentPassword, newPassword }, { headers: getAuthHeader() }),
  refreshToken: (refreshToken) =>
    axios.post(`${API_BASE_URL}/auth/refresh-token`, { refreshToken }),
  logout: () =>
    axios.post(`${API_BASE_URL}/auth/logout`, {}, { headers: getAuthHeader() }),
  updateProfile: (data) =>
    axios.put(`${API_BASE_URL}/auth/profile`, data, { headers: getAuthHeader() }),
};

// Projects
export const projectService = {
  getAll: () => axios.get(`${API_BASE_URL}/projects`, { headers: getAuthHeader() }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/projects/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/projects`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/projects/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/projects/${id}`, { headers: getAuthHeader() }),
  getTasks: (id) =>
    axios.get(`${API_BASE_URL}/projects/${id}/tasks`, { headers: getAuthHeader() }),
  // Members
  addMember: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/members`, data, { headers: getAuthHeader() }),
  removeMember: (id, memberId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/members/${memberId}`, { headers: getAuthHeader() }),
  updateMemberRole: (id, memberId, data) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/members/${memberId}`, data, { headers: getAuthHeader() }),
  // Updates
  addUpdate: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/updates`, data, { headers: getAuthHeader() }),
  deleteUpdate: (id, updateId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/updates/${updateId}`, { headers: getAuthHeader() }),
  // Milestones
  addMilestone: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/milestones`, data, { headers: getAuthHeader() }),
  updateMilestone: (id, milestoneId, data) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/milestones/${milestoneId}`, data, { headers: getAuthHeader() }),
  deleteMilestone: (id, milestoneId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/milestones/${milestoneId}`, { headers: getAuthHeader() }),
  // Risks
  addRisk: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/risks`, data, { headers: getAuthHeader() }),
  updateRisk: (id, riskId, data) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/risks/${riskId}`, data, { headers: getAuthHeader() }),
  deleteRisk: (id, riskId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/risks/${riskId}`, { headers: getAuthHeader() }),
  // Meetings
  addMeeting: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/meetings`, data, { headers: getAuthHeader() }),
  deleteMeeting: (id, meetingId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/meetings/${meetingId}`, { headers: getAuthHeader() }),
  // Deliverables
  addDeliverable: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/deliverables`, data, { headers: getAuthHeader() }),
  updateDeliverable: (id, deliverableId, data) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/deliverables/${deliverableId}`, data, { headers: getAuthHeader() }),
  deleteDeliverable: (id, deliverableId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/deliverables/${deliverableId}`, { headers: getAuthHeader() }),
  // Lessons
  addLesson: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/lessons`, data, { headers: getAuthHeader() }),
  deleteLesson: (id, lessonId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/lessons/${lessonId}`, { headers: getAuthHeader() }),
  // Costs & Revenue
  addCost: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/costs`, data, { headers: getAuthHeader() }),
  deleteCost: (id, costId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/costs/${costId}`, { headers: getAuthHeader() }),
  updateRevenue: (id, revenue) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/revenue`, { revenue }, { headers: getAuthHeader() }),
  // Objectives (PMBOK)
  addObjective: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/objectives`, data, { headers: getAuthHeader() }),
  updateObjective: (id, objectiveId, data) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/objectives/${objectiveId}`, data, { headers: getAuthHeader() }),
  deleteObjective: (id, objectiveId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/objectives/${objectiveId}`, { headers: getAuthHeader() }),
  // Success Criteria
  addSuccessCriterion: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/success-criteria`, data, { headers: getAuthHeader() }),
  updateSuccessCriterion: (id, criterionId, data) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/success-criteria/${criterionId}`, data, { headers: getAuthHeader() }),
  deleteSuccessCriterion: (id, criterionId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/success-criteria/${criterionId}`, { headers: getAuthHeader() }),
  // Stakeholders (PMBOK)
  addStakeholder: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/stakeholders`, data, { headers: getAuthHeader() }),
  updateStakeholder: (id, stakeholderId, data) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/stakeholders/${stakeholderId}`, data, { headers: getAuthHeader() }),
  deleteStakeholder: (id, stakeholderId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/stakeholders/${stakeholderId}`, { headers: getAuthHeader() }),
  // Change Requests (PRINCE2)
  addChangeRequest: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/change-requests`, data, { headers: getAuthHeader() }),
  updateChangeRequest: (id, changeRequestId, data) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/change-requests/${changeRequestId}`, data, { headers: getAuthHeader() }),
  deleteChangeRequest: (id, changeRequestId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/change-requests/${changeRequestId}`, { headers: getAuthHeader() }),
  // Action Plan (خطة العمل)
  addActionItem: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/action-plan`, data, { headers: getAuthHeader() }),
  updateActionItem: (id, itemId, data) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/action-plan/${itemId}`, data, { headers: getAuthHeader() }),
  deleteActionItem: (id, itemId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/action-plan/${itemId}`, { headers: getAuthHeader() }),
  // Auto-progress
  recalculateProgress: (id) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/recalculate-progress`, {}, { headers: getAuthHeader() }),
  // Attachments (مرفقات)
  uploadAttachment: (id, formData) =>
    axios.post(`${API_BASE_URL}/projects/${id}/attachments`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    }),
  deleteAttachment: (id, attachmentId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/attachments/${attachmentId}`, { headers: getAuthHeader() }),
  // Sprints (سبرنت)
  addSprint: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/sprints`, data, { headers: getAuthHeader() }),
  updateSprint: (id, sprintId, data) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/sprints/${sprintId}`, data, { headers: getAuthHeader() }),
  deleteSprint: (id, sprintId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/sprints/${sprintId}`, { headers: getAuthHeader() }),
  // Quality Checklists (قوائم فحص الجودة)
  addChecklist: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/checklists`, data, { headers: getAuthHeader() }),
  updateChecklistItem: (id, checklistId, itemId, data) =>
    axios.patch(`${API_BASE_URL}/projects/${id}/checklists/${checklistId}/items/${itemId}`, data, { headers: getAuthHeader() }),
  deleteChecklist: (id, checklistId) =>
    axios.delete(`${API_BASE_URL}/projects/${id}/checklists/${checklistId}`, { headers: getAuthHeader() }),
  // Templates (قوالب)
  saveAsTemplate: (id, data) =>
    axios.post(`${API_BASE_URL}/projects/${id}/save-template`, data, { headers: getAuthHeader() }),
  getTemplates: () =>
    axios.get(`${API_BASE_URL}/projects/templates/list`, { headers: getAuthHeader() }),
  createFromTemplate: (templateId, data) =>
    axios.post(`${API_BASE_URL}/projects/templates/${templateId}/create`, data, { headers: getAuthHeader() }),
};

// Project Cost Calculator (AI)
export const projectCostService = {
  getDashboard: () => axios.get(`${API_BASE_URL}/project-costs/dashboard`, { headers: getAuthHeader() }),
  getPredictions: () => axios.get(`${API_BASE_URL}/project-costs/ai-predictions`, { headers: getAuthHeader() }),
  getAnalysis: (id) => axios.get(`${API_BASE_URL}/project-costs/${id}/analysis`, { headers: getAuthHeader() }),
  estimate: (data) => axios.post(`${API_BASE_URL}/project-costs/estimate`, data, { headers: getAuthHeader() }),
  compare: (projectIds) => axios.post(`${API_BASE_URL}/project-costs/compare`, { projectIds }, { headers: getAuthHeader() }),
};

// Tasks
export const taskService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/tasks`, { headers: getAuthHeader(), params }),
  getActive: () =>
    axios.get(`${API_BASE_URL}/tasks`, { headers: getAuthHeader(), params: { status: 'in-progress' } }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/tasks`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/tasks/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/tasks/${id}`, { headers: getAuthHeader() }),
  addComment: (id, text) =>
    axios.post(`${API_BASE_URL}/tasks/${id}/comments`, { text }, { headers: getAuthHeader() }),
  assign: (id, userId) =>
    axios.post(`${API_BASE_URL}/tasks/${id}/assign`, { userId }, { headers: getAuthHeader() }),
  // Time Tracking (تتبع الوقت)
  addTimeLog: (id, data) =>
    axios.post(`${API_BASE_URL}/tasks/${id}/time-logs`, data, { headers: getAuthHeader() }),
  deleteTimeLog: (id, logId) =>
    axios.delete(`${API_BASE_URL}/tasks/${id}/time-logs/${logId}`, { headers: getAuthHeader() }),
  // Checklist (قائمة فحص)
  addChecklistItem: (id, text) =>
    axios.post(`${API_BASE_URL}/tasks/${id}/checklist`, { text }, { headers: getAuthHeader() }),
  toggleChecklistItem: (id, itemId) =>
    axios.patch(`${API_BASE_URL}/tasks/${id}/checklist/${itemId}/toggle`, {}, { headers: getAuthHeader() }),
};

// Invoices
export const invoiceService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/invoices`, { headers: getAuthHeader(), params }),
  getPending: () =>
    axios.get(`${API_BASE_URL}/invoices`, { headers: getAuthHeader(), params: { status: 'pending' } }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/invoices/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/invoices`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/invoices/${id}`, data, { headers: getAuthHeader() }),
  send: (id) =>
    axios.post(`${API_BASE_URL}/invoices/${id}/send`, {}, { headers: getAuthHeader() }),
  markAsPaid: (id) =>
    axios.post(
      `${API_BASE_URL}/invoices/${id}/mark-paid`,
      {},
      { headers: getAuthHeader() }
    ),
  getNextNumber: () =>
    axios.get(`${API_BASE_URL}/invoices/next-number`, { headers: getAuthHeader() }),
  getSellerInfo: () =>
    axios.get(`${API_BASE_URL}/invoices/seller-info`, { headers: getAuthHeader() }),
  getZatcaStats: () =>
    axios.get(`${API_BASE_URL}/invoices/zatca-stats`, { headers: getAuthHeader() }),
  cancel: (id) =>
    axios.post(`${API_BASE_URL}/invoices/${id}/cancel`, {}, { headers: getAuthHeader() }),
};

// Audit Log (سجل المراجعة)
export const auditService = {
  getLogs: (params) =>
    axios.get(`${API_BASE_URL}/audit`, { headers: getAuthHeader(), params }),
  getStats: () =>
    axios.get(`${API_BASE_URL}/audit/stats`, { headers: getAuthHeader() }),
};

// Inventory
export const inventoryService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/inventory`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/inventory/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/inventory`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/inventory/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/inventory/${id}`, { headers: getAuthHeader() }),
  getLowStock: () =>
    axios.get(`${API_BASE_URL}/inventory/low-stock`, { headers: getAuthHeader() }),
  updateStock: (id, data) =>
    axios.patch(`${API_BASE_URL}/inventory/${id}/stock`, data, { headers: getAuthHeader() }),
  getStats: () =>
    axios.get(`${API_BASE_URL}/inventory/stats`, { headers: getAuthHeader() }),
};

// Organization
export const organizationService = {
  getOrganization: () =>
    axios.get(`${API_BASE_URL}/organization`, { headers: getAuthHeader() }),
  update: (data) =>
    axios.put(`${API_BASE_URL}/organization`, data, { headers: getAuthHeader() }),
  getMembers: () =>
    axios.get(`${API_BASE_URL}/organization/members`, { headers: getAuthHeader() }),
  addMember: (data) =>
    axios.post(`${API_BASE_URL}/organization/members`, data, { headers: getAuthHeader() }),
  removeMember: (memberId) =>
    axios.delete(`${API_BASE_URL}/organization/members/${memberId}`, { headers: getAuthHeader() }),
  updateMemberPermissions: (memberId, permissions) =>
    axios.patch(`${API_BASE_URL}/organization/members/${memberId}/permissions`, { permissions }, { headers: getAuthHeader() }),
  updateMemberRole: (memberId, role) =>
    axios.patch(`${API_BASE_URL}/organization/members/${memberId}/role`, { role }, { headers: getAuthHeader() }),
  updateModules: (modules) =>
    axios.put(`${API_BASE_URL}/organization/modules`, { selectedModules: modules }, { headers: getAuthHeader() }),
  upgradePlan: (newPlan) =>
    axios.post(`${API_BASE_URL}/organization/upgrade-plan`, { newPlan }, { headers: getAuthHeader() }),
  addExtraUsers: (count, paymentConfirmed) =>
    axios.post(`${API_BASE_URL}/organization/add-extra-users`, { count, paymentConfirmed }, { headers: getAuthHeader() }),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return axios.post(`${API_BASE_URL}/organization/logo`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: () =>
    axios.delete(`${API_BASE_URL}/organization/logo`, { headers: getAuthHeader() }),
};

// Quotations (عروض الأسعار)
export const quotationService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/quotations`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/quotations/${id}`, { headers: getAuthHeader() }),
  create: (formData) =>
    axios.post(`${API_BASE_URL}/quotations`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    }),
  update: (id, formData) =>
    axios.put(`${API_BASE_URL}/quotations/${id}`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/quotations/${id}`, { headers: getAuthHeader() }),
  deleteAttachment: (id, attId) =>
    axios.delete(`${API_BASE_URL}/quotations/${id}/attachments/${attId}`, { headers: getAuthHeader() }),
  downloadAttachment: (id, attId) =>
    `${API_BASE_URL}/quotations/${id}/attachments/${attId}/download`,
};

// Customers (العملاء)
export const customerService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/customers`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/customers/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/customers`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/customers/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/customers/${id}`, { headers: getAuthHeader() }),
};

// Sales
export const salesService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/sales`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/sales/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/sales`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/sales/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/sales/${id}`, { headers: getAuthHeader() }),
  getStats: () =>
    axios.get(`${API_BASE_URL}/sales/stats`, { headers: getAuthHeader() }),
};

// CRM
export const crmService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/crm`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/crm/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/crm`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/crm/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/crm/${id}`, { headers: getAuthHeader() }),
  addActivity: (id, data) =>
    axios.post(`${API_BASE_URL}/crm/${id}/activities`, data, { headers: getAuthHeader() }),
  addDeal: (id, data) =>
    axios.post(`${API_BASE_URL}/crm/${id}/deals`, data, { headers: getAuthHeader() }),
  updateDeal: (id, dealId, data) =>
    axios.patch(`${API_BASE_URL}/crm/${id}/deals/${dealId}`, data, { headers: getAuthHeader() }),
  convertToProject: (id, dealId) =>
    axios.post(`${API_BASE_URL}/crm/${id}/deals/${dealId}/convert`, {}, { headers: getAuthHeader() }),
  getStats: () =>
    axios.get(`${API_BASE_URL}/crm/stats`, { headers: getAuthHeader() }),
  getPipeline: () =>
    axios.get(`${API_BASE_URL}/crm/pipeline`, { headers: getAuthHeader() }),
};

// Manufacturing
export const manufacturingService = {
  // أوامر الإنتاج
  getOrders: (params) =>
    axios.get(`${API_BASE_URL}/manufacturing/orders`, { headers: getAuthHeader(), params }),
  getOrderById: (id) =>
    axios.get(`${API_BASE_URL}/manufacturing/orders/${id}`, { headers: getAuthHeader() }),
  createOrder: (data) =>
    axios.post(`${API_BASE_URL}/manufacturing/orders`, data, { headers: getAuthHeader() }),
  updateOrder: (id, data) =>
    axios.put(`${API_BASE_URL}/manufacturing/orders/${id}`, data, { headers: getAuthHeader() }),
  deleteOrder: (id) =>
    axios.delete(`${API_BASE_URL}/manufacturing/orders/${id}`, { headers: getAuthHeader() }),
  addStage: (id, data) =>
    axios.post(`${API_BASE_URL}/manufacturing/orders/${id}/stages`, data, { headers: getAuthHeader() }),
  updateStage: (id, stageId, data) =>
    axios.patch(`${API_BASE_URL}/manufacturing/orders/${id}/stages/${stageId}`, data, { headers: getAuthHeader() }),
  addQualityCheck: (id, data) =>
    axios.post(`${API_BASE_URL}/manufacturing/orders/${id}/quality-checks`, data, { headers: getAuthHeader() }),
  // المنتجات
  getProducts: (params) =>
    axios.get(`${API_BASE_URL}/manufacturing/products`, { headers: getAuthHeader(), params }),
  createProduct: (data) =>
    axios.post(`${API_BASE_URL}/manufacturing/products`, data, { headers: getAuthHeader() }),
  updateProduct: (id, data) =>
    axios.put(`${API_BASE_URL}/manufacturing/products/${id}`, data, { headers: getAuthHeader() }),
  deleteProduct: (id) =>
    axios.delete(`${API_BASE_URL}/manufacturing/products/${id}`, { headers: getAuthHeader() }),
  // مراكز العمل
  getWorkCenters: (params) =>
    axios.get(`${API_BASE_URL}/manufacturing/work-centers`, { headers: getAuthHeader(), params }),
  createWorkCenter: (data) =>
    axios.post(`${API_BASE_URL}/manufacturing/work-centers`, data, { headers: getAuthHeader() }),
  updateWorkCenter: (id, data) =>
    axios.put(`${API_BASE_URL}/manufacturing/work-centers/${id}`, data, { headers: getAuthHeader() }),
  deleteWorkCenter: (id) =>
    axios.delete(`${API_BASE_URL}/manufacturing/work-centers/${id}`, { headers: getAuthHeader() }),
  addMaintenance: (id, data) =>
    axios.post(`${API_BASE_URL}/manufacturing/work-centers/${id}/maintenance`, data, { headers: getAuthHeader() }),
  // إحصائيات
  getStats: () =>
    axios.get(`${API_BASE_URL}/manufacturing/stats`, { headers: getAuthHeader() }),
};

// Accounting (المحاسبة)
export const accountingService = {
  // إحصائيات
  getStats: () =>
    axios.get(`${API_BASE_URL}/accounting/stats`, { headers: getAuthHeader() }),
  // دليل الحسابات
  getAccounts: () =>
    axios.get(`${API_BASE_URL}/accounting/accounts`, { headers: getAuthHeader() }),
  createAccount: (data) =>
    axios.post(`${API_BASE_URL}/accounting/accounts`, data, { headers: getAuthHeader() }),
  updateAccount: (id, data) =>
    axios.put(`${API_BASE_URL}/accounting/accounts/${id}`, data, { headers: getAuthHeader() }),
  deleteAccount: (id) =>
    axios.delete(`${API_BASE_URL}/accounting/accounts/${id}`, { headers: getAuthHeader() }),
  // القيود اليومية
  getJournalEntries: () =>
    axios.get(`${API_BASE_URL}/accounting/journal-entries`, { headers: getAuthHeader() }),
  getJournalEntry: (id) =>
    axios.get(`${API_BASE_URL}/accounting/journal-entries/${id}`, { headers: getAuthHeader() }),
  createJournalEntry: (data) =>
    axios.post(`${API_BASE_URL}/accounting/journal-entries`, data, { headers: getAuthHeader() }),
  postJournalEntry: (id) =>
    axios.post(`${API_BASE_URL}/accounting/journal-entries/${id}/post`, {}, { headers: getAuthHeader() }),
  reverseJournalEntry: (id) =>
    axios.post(`${API_BASE_URL}/accounting/journal-entries/${id}/reverse`, {}, { headers: getAuthHeader() }),
  // المدفوعات
  getPayments: () =>
    axios.get(`${API_BASE_URL}/accounting/payments`, { headers: getAuthHeader() }),
  createPayment: (data) =>
    axios.post(`${API_BASE_URL}/accounting/payments`, data, { headers: getAuthHeader() }),
  updatePayment: (id, data) =>
    axios.put(`${API_BASE_URL}/accounting/payments/${id}`, data, { headers: getAuthHeader() }),
  deletePayment: (id) =>
    axios.delete(`${API_BASE_URL}/accounting/payments/${id}`, { headers: getAuthHeader() }),
  // الأقسام / مراكز التكلفة
  getDepartments: () =>
    axios.get(`${API_BASE_URL}/accounting/departments`, { headers: getAuthHeader() }),
  getDepartment: (id) =>
    axios.get(`${API_BASE_URL}/accounting/departments/${id}`, { headers: getAuthHeader() }),
  createDepartment: (data) =>
    axios.post(`${API_BASE_URL}/accounting/departments`, data, { headers: getAuthHeader() }),
  updateDepartment: (id, data) =>
    axios.put(`${API_BASE_URL}/accounting/departments/${id}`, data, { headers: getAuthHeader() }),
  deleteDepartment: (id) =>
    axios.delete(`${API_BASE_URL}/accounting/departments/${id}`, { headers: getAuthHeader() }),
  // التقارير
  getTrialBalance: () =>
    axios.get(`${API_BASE_URL}/accounting/trial-balance`, { headers: getAuthHeader() }),
  getIncomeStatement: (params) =>
    axios.get(`${API_BASE_URL}/accounting/income-statement`, { headers: getAuthHeader(), params }),
  getBalanceSheet: (params) =>
    axios.get(`${API_BASE_URL}/accounting/balance-sheet`, { headers: getAuthHeader(), params }),
  getCashFlow: (params) =>
    axios.get(`${API_BASE_URL}/accounting/cash-flow`, { headers: getAuthHeader(), params }),
  getGeneralLedger: (params) =>
    axios.get(`${API_BASE_URL}/accounting/general-ledger`, { headers: getAuthHeader(), params }),
  getVATReport: (params) =>
    axios.get(`${API_BASE_URL}/accounting/vat-report`, { headers: getAuthHeader(), params }),
  getFinancialSummary: (params) =>
    axios.get(`${API_BASE_URL}/accounting/financial-summary`, { headers: getAuthHeader(), params }),
};

// الكاشير (نقطة البيع)
export const cashierService = {
  getStats: () =>
    axios.get(`${API_BASE_URL}/cashier/stats`, { headers: getAuthHeader() }),
  getSessions: () =>
    axios.get(`${API_BASE_URL}/cashier/sessions`, { headers: getAuthHeader() }),
  getOpenSession: () =>
    axios.get(`${API_BASE_URL}/cashier/sessions/open`, { headers: getAuthHeader() }),
  getSession: (id) =>
    axios.get(`${API_BASE_URL}/cashier/sessions/${id}`, { headers: getAuthHeader() }),
  openSession: (data) =>
    axios.post(`${API_BASE_URL}/cashier/sessions`, data, { headers: getAuthHeader() }),
  closeSession: (id, data) =>
    axios.patch(`${API_BASE_URL}/cashier/sessions/${id}/close`, data, { headers: getAuthHeader() }),
  addTransaction: (sessionId, data) =>
    axios.post(`${API_BASE_URL}/cashier/sessions/${sessionId}/transactions`, data, { headers: getAuthHeader() }),
  refundTransaction: (sessionId, txId, data) =>
    axios.patch(`${API_BASE_URL}/cashier/sessions/${sessionId}/transactions/${txId}/refund`, data, { headers: getAuthHeader() }),

  // ═══ منتجات الكاشير (مستقل) ═══
  getProducts: () =>
    axios.get(`${API_BASE_URL}/cashier/products`, { headers: getAuthHeader() }),
  getProductByBarcode: (barcode) =>
    axios.get(`${API_BASE_URL}/cashier/products/barcode/${encodeURIComponent(barcode)}`, { headers: getAuthHeader() }),
  createProduct: (data) =>
    axios.post(`${API_BASE_URL}/cashier/products`, data, { headers: getAuthHeader() }),
  updateProduct: (id, data) =>
    axios.put(`${API_BASE_URL}/cashier/products/${id}`, data, { headers: getAuthHeader() }),
  deleteProduct: (id) =>
    axios.delete(`${API_BASE_URL}/cashier/products/${id}`, { headers: getAuthHeader() }),
  updateProductStock: (id, data) =>
    axios.patch(`${API_BASE_URL}/cashier/products/${id}/stock`, data, { headers: getAuthHeader() }),
  getProductsStats: () =>
    axios.get(`${API_BASE_URL}/cashier/products-stats`, { headers: getAuthHeader() }),

  // ═══ الخزنة ═══
  getSafe: () =>
    axios.get(`${API_BASE_URL}/cashier/safe`, { headers: getAuthHeader() }),
  depositToSafe: (data) =>
    axios.post(`${API_BASE_URL}/cashier/safe/deposit`, data, { headers: getAuthHeader() }),
  withdrawFromSafe: (data) =>
    axios.post(`${API_BASE_URL}/cashier/safe/withdraw`, data, { headers: getAuthHeader() }),
};

// المصروفات
export const expenseService = {
  getStats: () =>
    axios.get(`${API_BASE_URL}/expenses/stats`, { headers: getAuthHeader() }),
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/expenses`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/expenses/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/expenses`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/expenses/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/expenses/${id}`, { headers: getAuthHeader() }),
};

// المشتريات والموردين
export const purchaseService = {
  // الموردين
  getSuppliers: (params) =>
    axios.get(`${API_BASE_URL}/purchases/suppliers`, { headers: getAuthHeader(), params }),
  getSupplierStats: () =>
    axios.get(`${API_BASE_URL}/purchases/suppliers/stats`, { headers: getAuthHeader() }),
  getSupplierById: (id) =>
    axios.get(`${API_BASE_URL}/purchases/suppliers/${id}`, { headers: getAuthHeader() }),
  createSupplier: (data) =>
    axios.post(`${API_BASE_URL}/purchases/suppliers`, data, { headers: getAuthHeader() }),
  updateSupplier: (id, data) =>
    axios.put(`${API_BASE_URL}/purchases/suppliers/${id}`, data, { headers: getAuthHeader() }),
  deleteSupplier: (id) =>
    axios.delete(`${API_BASE_URL}/purchases/suppliers/${id}`, { headers: getAuthHeader() }),
  // أوامر الشراء
  getStats: () =>
    axios.get(`${API_BASE_URL}/purchases/stats`, { headers: getAuthHeader() }),
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/purchases`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/purchases/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/purchases`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/purchases/${id}`, data, { headers: getAuthHeader() }),
  approve: (id) =>
    axios.post(`${API_BASE_URL}/purchases/${id}/approve`, {}, { headers: getAuthHeader() }),
  receive: (id, data) =>
    axios.post(`${API_BASE_URL}/purchases/${id}/receive`, data, { headers: getAuthHeader() }),
  cancel: (id) =>
    axios.post(`${API_BASE_URL}/purchases/${id}/cancel`, {}, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/purchases/${id}`, { headers: getAuthHeader() }),
};

// الجرد
export const stocktakeService = {
  getStats: () =>
    axios.get(`${API_BASE_URL}/stocktake/stats`, { headers: getAuthHeader() }),
  getAll: () =>
    axios.get(`${API_BASE_URL}/stocktake`, { headers: getAuthHeader() }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/stocktake/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/stocktake`, data, { headers: getAuthHeader() }),
  updateItemCount: (id, itemId, data) =>
    axios.patch(`${API_BASE_URL}/stocktake/${id}/items/${itemId}`, data, { headers: getAuthHeader() }),
  complete: (id) =>
    axios.patch(`${API_BASE_URL}/stocktake/${id}/complete`, {}, { headers: getAuthHeader() }),
  approve: (id, data) =>
    axios.patch(`${API_BASE_URL}/stocktake/${id}/approve`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/stocktake/${id}`, { headers: getAuthHeader() }),
};

// Clinics
export const clinicService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/clinics`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/clinics/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/clinics`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/clinics/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/clinics/${id}`, { headers: getAuthHeader() }),
  addDoctor: (clinicId, doctorId) =>
    axios.post(`${API_BASE_URL}/clinics/${clinicId}/doctors`, { doctorId }, { headers: getAuthHeader() }),
  getStats: (id) =>
    axios.get(`${API_BASE_URL}/clinics/${id}/stats`, { headers: getAuthHeader() }),
  getDashboard: () =>
    axios.get(`${API_BASE_URL}/clinics/dashboard`, { headers: getAuthHeader() }),
};

// Doctors
export const doctorService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/doctors`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/doctors/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/doctors`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/doctors/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/doctors/${id}`, { headers: getAuthHeader() }),
  getDashboard: () =>
    axios.get(`${API_BASE_URL}/doctors/dashboard`, { headers: getAuthHeader() }),
  getOnDuty: () =>
    axios.get(`${API_BASE_URL}/doctors/on-duty`, { headers: getAuthHeader() }),
  toggleDuty: (id, data) =>
    axios.patch(`${API_BASE_URL}/doctors/${id}/toggle-duty`, data, { headers: getAuthHeader() }),
  updateAvailability: (id, data) =>
    axios.patch(`${API_BASE_URL}/doctors/${id}/availability`, data, { headers: getAuthHeader() }),
  updateRating: (id, data) =>
    axios.patch(`${API_BASE_URL}/doctors/${id}/rating`, data, { headers: getAuthHeader() }),
};

// ==================== خدمة مرضى العيادات ====================
export const clinicPatientService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/clinic-patients`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/clinic-patients/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/clinic-patients`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/clinic-patients/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/clinic-patients/${id}`, { headers: getAuthHeader() }),
  addVisit: (id, data) =>
    axios.post(`${API_BASE_URL}/clinic-patients/${id}/visits`, data, { headers: getAuthHeader() }),
  getStats: () =>
    axios.get(`${API_BASE_URL}/clinic-patients/stats`, { headers: getAuthHeader() }),
};

// ==================== خدمة الفوترة الطبية ====================
export const medicalBillingService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/medical-billing`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/medical-billing/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/medical-billing`, data, { headers: getAuthHeader() }),
  pay: (id, data) =>
    axios.patch(`${API_BASE_URL}/medical-billing/${id}/pay`, data, { headers: getAuthHeader() }),
  cancel: (id) =>
    axios.patch(`${API_BASE_URL}/medical-billing/${id}/cancel`, {}, { headers: getAuthHeader() }),
  getStats: () =>
    axios.get(`${API_BASE_URL}/medical-billing/stats`, { headers: getAuthHeader() }),
};

// ==================== خدمة التقارير الطبية ====================
export const healthcareReportsService = {
  getOverview: () =>
    axios.get(`${API_BASE_URL}/healthcare-reports/overview`, { headers: getAuthHeader() }),
};

// ==================== خدمة موظفي العيادات ====================
export const healthcareStaffService = {
  getAll: () =>
    axios.get(`${API_BASE_URL}/healthcare-staff`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/healthcare-staff`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/healthcare-staff/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/healthcare-staff/${id}`, { headers: getAuthHeader() }),
  getStats: () =>
    axios.get(`${API_BASE_URL}/healthcare-staff/stats`, { headers: getAuthHeader() }),
};

// ==================== خدمة المواعيد ====================
export const appointmentService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/appointments`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/appointments/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/appointments`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/appointments/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/appointments/${id}`, { headers: getAuthHeader() }),
  getStats: () =>
    axios.get(`${API_BASE_URL}/appointments/stats`, { headers: getAuthHeader() }),
  getUpcoming: () =>
    axios.get(`${API_BASE_URL}/appointments/upcoming`, { headers: getAuthHeader() }),
  getToday: () =>
    axios.get(`${API_BASE_URL}/appointments/today`, { headers: getAuthHeader() }),
  updateStatus: (id, data) =>
    axios.patch(`${API_BASE_URL}/appointments/${id}/status`, data, { headers: getAuthHeader() }),
  cancel: (id, data) =>
    axios.patch(`${API_BASE_URL}/appointments/${id}/cancel`, data, { headers: getAuthHeader() }),
  complete: (id, data) =>
    axios.patch(`${API_BASE_URL}/appointments/${id}/complete`, data, { headers: getAuthHeader() }),
  clearAll: () =>
    axios.delete(`${API_BASE_URL}/appointments/clear-all`, { headers: getAuthHeader() }),
  getDoctorAppointments: (doctorId, params) =>
    axios.get(`${API_BASE_URL}/appointments/doctor/${doctorId}`, { headers: getAuthHeader(), params }),
};

// ════ خدمة شؤون الموظفين HR ════
export const hrService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/hr`, { headers: getAuthHeader(), params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/hr/${id}`, { headers: getAuthHeader() }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/hr`, data, { headers: getAuthHeader() }),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/hr/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/hr/${id}`, { headers: getAuthHeader() }),
  getStats: () =>
    axios.get(`${API_BASE_URL}/hr/stats`, { headers: getAuthHeader() }),
  getDepartmentStats: () =>
    axios.get(`${API_BASE_URL}/hr/departments/stats`, { headers: getAuthHeader() }),
  getPayrollReport: () =>
    axios.get(`${API_BASE_URL}/hr/payroll/report`, { headers: getAuthHeader() }),
  updateStatus: (id, data) =>
    axios.patch(`${API_BASE_URL}/hr/${id}/status`, data, { headers: getAuthHeader() }),
  updateSalary: (id, data) =>
    axios.patch(`${API_BASE_URL}/hr/${id}/salary`, data, { headers: getAuthHeader() }),
  updateLeave: (id, data) =>
    axios.patch(`${API_BASE_URL}/hr/${id}/leave`, data, { headers: getAuthHeader() }),
  updatePerformance: (id, data) =>
    axios.patch(`${API_BASE_URL}/hr/${id}/performance`, data, { headers: getAuthHeader() }),
  // ════ الإجازات ════
  getLeaves: (params) => axios.get(`${API_BASE_URL}/hr/leaves`, { headers: getAuthHeader(), params }),
  createLeave: (data) => axios.post(`${API_BASE_URL}/hr/leaves`, data, { headers: getAuthHeader() }),
  updateLeaveStatus: (id, data) => axios.patch(`${API_BASE_URL}/hr/leaves/${id}/status`, data, { headers: getAuthHeader() }),
  deleteLeaveReq: (id) => axios.delete(`${API_BASE_URL}/hr/leaves/${id}`, { headers: getAuthHeader() }),
  getLeaveStats: () => axios.get(`${API_BASE_URL}/hr/leaves/stats`, { headers: getAuthHeader() }),
  // ════ الأذونات ════
  getPermissions: (params) => axios.get(`${API_BASE_URL}/hr/permissions`, { headers: getAuthHeader(), params }),
  createPermission: (data) => axios.post(`${API_BASE_URL}/hr/permissions`, data, { headers: getAuthHeader() }),
  updatePermissionStatus: (id, data) => axios.patch(`${API_BASE_URL}/hr/permissions/${id}/status`, data, { headers: getAuthHeader() }),
  deletePermission: (id) => axios.delete(`${API_BASE_URL}/hr/permissions/${id}`, { headers: getAuthHeader() }),
  getPermissionStats: () => axios.get(`${API_BASE_URL}/hr/permissions/stats`, { headers: getAuthHeader() }),
  // ════ السلف ════
  getAdvances: (params) => axios.get(`${API_BASE_URL}/hr/advances`, { headers: getAuthHeader(), params }),
  createAdvance: (data) => axios.post(`${API_BASE_URL}/hr/advances`, data, { headers: getAuthHeader() }),
  updateAdvanceStatus: (id, data) => axios.patch(`${API_BASE_URL}/hr/advances/${id}/status`, data, { headers: getAuthHeader() }),
  deleteAdvance: (id) => axios.delete(`${API_BASE_URL}/hr/advances/${id}`, { headers: getAuthHeader() }),
  getAdvanceStats: () => axios.get(`${API_BASE_URL}/hr/advances/stats`, { headers: getAuthHeader() }),
  // ════ الترقيات ════
  getPromotions: (params) => axios.get(`${API_BASE_URL}/hr/promotions`, { headers: getAuthHeader(), params }),
  createPromotion: (data) => axios.post(`${API_BASE_URL}/hr/promotions`, data, { headers: getAuthHeader() }),
  updatePromotionStatus: (id, data) => axios.patch(`${API_BASE_URL}/hr/promotions/${id}/status`, data, { headers: getAuthHeader() }),
  deletePromotion: (id) => axios.delete(`${API_BASE_URL}/hr/promotions/${id}`, { headers: getAuthHeader() }),
  getPromotionStats: () => axios.get(`${API_BASE_URL}/hr/promotions/stats`, { headers: getAuthHeader() }),
  // ════ العقوبات ════
  getDisciplinaries: (params) => axios.get(`${API_BASE_URL}/hr/disciplinaries`, { headers: getAuthHeader(), params }),
  createDisciplinary: (data) => axios.post(`${API_BASE_URL}/hr/disciplinaries`, data, { headers: getAuthHeader() }),
  updateDisciplinaryStatus: (id, data) => axios.patch(`${API_BASE_URL}/hr/disciplinaries/${id}/status`, data, { headers: getAuthHeader() }),
  deleteDisciplinary: (id) => axios.delete(`${API_BASE_URL}/hr/disciplinaries/${id}`, { headers: getAuthHeader() }),

  getDisciplinaryStats: () => axios.get(`${API_BASE_URL}/hr/disciplinaries/stats`, { headers: getAuthHeader() }),
  // ════ الحضور والانصراف ════
  getGeofenceSettings: () => axios.get(`${API_BASE_URL}/hr/attendance/geofence`, { headers: getAuthHeader() }),
  updateGeofenceSettings: (data) => axios.put(`${API_BASE_URL}/hr/attendance/geofence`, data, { headers: getAuthHeader() }),
  checkIn: (data) => axios.post(`${API_BASE_URL}/hr/attendance/check-in`, data, { headers: getAuthHeader() }),
  checkOut: (data) => axios.post(`${API_BASE_URL}/hr/attendance/check-out`, data, { headers: getAuthHeader() }),
  getAttendance: (params) => axios.get(`${API_BASE_URL}/hr/attendance`, { headers: getAuthHeader(), params }),
  getTodayAttendance: () => axios.get(`${API_BASE_URL}/hr/attendance/today`, { headers: getAuthHeader() }),
  manualAttendance: (data) => axios.post(`${API_BASE_URL}/hr/attendance/manual`, data, { headers: getAuthHeader() }),
  getAttendanceStats: (params) => axios.get(`${API_BASE_URL}/hr/attendance/stats`, { headers: getAuthHeader(), params }),
  getEmployeeAttendance: (empId, params) => axios.get(`${API_BASE_URL}/hr/attendance/employee/${empId}`, { headers: getAuthHeader(), params }),
  // ════ الرواتب ════
  generatePayroll: (data) => axios.post(`${API_BASE_URL}/hr/payroll/generate`, data, { headers: getAuthHeader() }),
  getMonthlyPayroll: (params) => axios.get(`${API_BASE_URL}/hr/payroll/monthly`, { headers: getAuthHeader(), params }),
  getPayslip: (id) => axios.get(`${API_BASE_URL}/hr/payroll/${id}`, { headers: getAuthHeader() }),
  approvePayroll: (data) => axios.post(`${API_BASE_URL}/hr/payroll/approve`, data, { headers: getAuthHeader() }),
  markPayrollPaid: (data) => axios.post(`${API_BASE_URL}/hr/payroll/mark-paid`, data, { headers: getAuthHeader() }),
  deletePayroll: (id) => axios.delete(`${API_BASE_URL}/hr/payroll/${id}`, { headers: getAuthHeader() }),
  getPayrollStats: (params) => axios.get(`${API_BASE_URL}/hr/payroll/stats`, { headers: getAuthHeader(), params }),
  // ════ التحليلات ════
  getAnalytics: () => axios.get(`${API_BASE_URL}/hr/analytics`, { headers: getAuthHeader() }),
};

// Email Management
export const emailService = {
  getAll: (params) => axios.get(`${API_BASE_URL}/emails`, { headers: getAuthHeader(), params }),
  getStats: () => axios.get(`${API_BASE_URL}/emails/stats`, { headers: getAuthHeader() }),
  getSettings: () => axios.get(`${API_BASE_URL}/emails/settings`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_BASE_URL}/emails`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_BASE_URL}/emails/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_BASE_URL}/emails/${id}`, { headers: getAuthHeader() }),
};

// Stripe
export const stripeService = {
  createCheckoutSession: (priceId) =>
    axios.post(`${API_BASE_URL}/stripe/create-checkout-session`, { priceId }, { headers: getAuthHeader() }),
  getSubscription: () =>
    axios.get(`${API_BASE_URL}/stripe/subscription`, { headers: getAuthHeader() }),
  cancelSubscription: () =>
    axios.post(`${API_BASE_URL}/stripe/cancel-subscription`, {}, { headers: getAuthHeader() }),
  changePlan: (newPriceId) =>
    axios.post(`${API_BASE_URL}/stripe/change-plan`, { newPriceId }, { headers: getAuthHeader() }),
  verifySession: (sessionId) =>
    axios.get(`${API_BASE_URL}/stripe/verify-session/${sessionId}`, { headers: getAuthHeader() }),
};

// ════ Paylink بيلينك ════
export const paylinkService = {
  // التحقق من حالة بيلينك
  getStatus: () =>
    axios.get(`${API_BASE_URL}/paylink/status`),
  // الحصول على أسعار الباقات
  getPrices: () =>
    axios.get(`${API_BASE_URL}/paylink/prices`),
  // إنشاء اشتراك جديد
  createSubscription: (planId, billingCycle = 'monthly') =>
    axios.post(`${API_BASE_URL}/paylink/create-subscription`, { planId, billingCycle }, { headers: getAuthHeader() }),
  // ترقية الباقة
  upgradePlan: (newPlanId, billingCycle = 'monthly') =>
    axios.post(`${API_BASE_URL}/paylink/upgrade-plan`, { newPlanId, billingCycle }, { headers: getAuthHeader() }),
  // إضافة مستخدمين/عيادات
  addAddon: (addonType, quantity = 1) =>
    axios.post(`${API_BASE_URL}/paylink/add-addon`, { addonType, quantity }, { headers: getAuthHeader() }),
  // التحقق من حالة الدفع
  verifyPayment: (transactionNo) =>
    axios.get(`${API_BASE_URL}/paylink/verify/${transactionNo}`, { headers: getAuthHeader() }),
  // سجل المدفوعات
  getHistory: () =>
    axios.get(`${API_BASE_URL}/paylink/history`, { headers: getAuthHeader() }),
};

// ════ Admin API ════
export const adminService = {
  getStats: () =>
    axios.get(`${API_BASE_URL}/admin/stats`, { headers: getAuthHeader() }),
  getOrganizations: (params) =>
    axios.get(`${API_BASE_URL}/admin/organizations`, { headers: getAuthHeader(), params }),
  updateOrganization: (orgId, data) =>
    axios.patch(`${API_BASE_URL}/admin/organizations/${orgId}`, data, { headers: getAuthHeader() }),
  getUsers: (params) =>
    axios.get(`${API_BASE_URL}/admin/users`, { headers: getAuthHeader(), params }),
  updateUser: (userId, data) =>
    axios.patch(`${API_BASE_URL}/admin/users/${userId}`, data, { headers: getAuthHeader() }),
};

// ════ Notifications API ════
export const notificationService = {
  getAll: (params) =>
    axios.get(`${API_BASE_URL}/notifications`, { headers: getAuthHeader(), params }),
  getUnreadCount: () =>
    axios.get(`${API_BASE_URL}/notifications/unread-count`, { headers: getAuthHeader() }),
  markAsRead: (notificationId) =>
    axios.patch(`${API_BASE_URL}/notifications/${notificationId}/read`, {}, { headers: getAuthHeader() }),
  markAllAsRead: () =>
    axios.patch(`${API_BASE_URL}/notifications/all/read`, {}, { headers: getAuthHeader() }),
  delete: (notificationId) =>
    axios.delete(`${API_BASE_URL}/notifications/${notificationId}`, { headers: getAuthHeader() }),
};

// ════ WhatsApp Chatbot API ════
export const whatsappService = {
  // إعدادات
  getSettings: () => axios.get(`${API_BASE_URL}/whatsapp/settings`, { headers: getAuthHeader() }),
  updateSettings: (data) => axios.put(`${API_BASE_URL}/whatsapp/settings`, data, { headers: getAuthHeader() }),
  toggleActive: () => axios.post(`${API_BASE_URL}/whatsapp/toggle`, {}, { headers: getAuthHeader() }),
  testConnection: () => axios.post(`${API_BASE_URL}/whatsapp/test`, {}, { headers: getAuthHeader() }),
  // القوائم التفاعلية
  addMenuItem: (data) => axios.post(`${API_BASE_URL}/whatsapp/menu`, data, { headers: getAuthHeader() }),
  updateMenuItem: (itemId, data) => axios.put(`${API_BASE_URL}/whatsapp/menu/${itemId}`, data, { headers: getAuthHeader() }),
  deleteMenuItem: (itemId) => axios.delete(`${API_BASE_URL}/whatsapp/menu/${itemId}`, { headers: getAuthHeader() }),
  // الردود التلقائية
  addAutoReply: (data) => axios.post(`${API_BASE_URL}/whatsapp/auto-replies`, data, { headers: getAuthHeader() }),
  updateAutoReply: (replyId, data) => axios.put(`${API_BASE_URL}/whatsapp/auto-replies/${replyId}`, data, { headers: getAuthHeader() }),
  deleteAutoReply: (replyId) => axios.delete(`${API_BASE_URL}/whatsapp/auto-replies/${replyId}`, { headers: getAuthHeader() }),
  // المحادثات
  getConversations: () => axios.get(`${API_BASE_URL}/whatsapp/conversations`, { headers: getAuthHeader() }),
  getConversation: (id) => axios.get(`${API_BASE_URL}/whatsapp/conversations/${id}`, { headers: getAuthHeader() }),
  sendMessage: (conversationId, message) => axios.post(`${API_BASE_URL}/whatsapp/conversations/${conversationId}/send`, { message }, { headers: getAuthHeader() }),
  // الإحصائيات
  getStats: () => axios.get(`${API_BASE_URL}/whatsapp/stats`, { headers: getAuthHeader() }),
};

// ════ Hospital Management API (HL7 FHIR) ════
export const hospitalService = {
  // Hospital CRUD
  getAll: (params) => axios.get(`${API_BASE_URL}/hospitals`, { headers: getAuthHeader(), params }),
  getById: (id) => axios.get(`${API_BASE_URL}/hospitals/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_BASE_URL}/hospitals`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_BASE_URL}/hospitals/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_BASE_URL}/hospitals/${id}`, { headers: getAuthHeader() }),
  // Dashboard & Stats
  getDashboard: (id) => axios.get(`${API_BASE_URL}/hospitals/${id}/dashboard`, { headers: getAuthHeader() }),
  getStats: (id) => axios.get(`${API_BASE_URL}/hospitals/${id}/stats`, { headers: getAuthHeader() }),
  // Departments
  getDepartments: (id) => axios.get(`${API_BASE_URL}/hospitals/${id}/departments`, { headers: getAuthHeader() }),
  createDepartment: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/departments`, data, { headers: getAuthHeader() }),
  updateDepartment: (id, deptId, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/departments/${deptId}`, data, { headers: getAuthHeader() }),
  deleteDepartment: (id, deptId) => axios.delete(`${API_BASE_URL}/hospitals/${id}/departments/${deptId}`, { headers: getAuthHeader() }),
  // Beds
  getBeds: (id, params) => axios.get(`${API_BASE_URL}/hospitals/${id}/beds`, { headers: getAuthHeader(), params }),
  getBedMap: (id) => axios.get(`${API_BASE_URL}/hospitals/${id}/beds/map`, { headers: getAuthHeader() }),
  createBed: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/beds`, data, { headers: getAuthHeader() }),
  updateBed: (id, bedId, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/beds/${bedId}`, data, { headers: getAuthHeader() }),
  deleteBed: (id, bedId) => axios.delete(`${API_BASE_URL}/hospitals/${id}/beds/${bedId}`, { headers: getAuthHeader() }),
  // Patients
  getPatients: (id, params) => axios.get(`${API_BASE_URL}/hospitals/${id}/patients`, { headers: getAuthHeader(), params }),
  getPatientById: (id, patientId) => axios.get(`${API_BASE_URL}/hospitals/${id}/patients/${patientId}`, { headers: getAuthHeader() }),
  createPatient: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/patients`, data, { headers: getAuthHeader() }),
  updatePatient: (id, patientId, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/patients/${patientId}`, data, { headers: getAuthHeader() }),
  deletePatient: (id, patientId) => axios.delete(`${API_BASE_URL}/hospitals/${id}/patients/${patientId}`, { headers: getAuthHeader() }),
  // Patient Clinical
  addVitalSigns: (id, patientId, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/patients/${patientId}/vitals`, data, { headers: getAuthHeader() }),
  addLabResult: (id, patientId, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/patients/${patientId}/labs`, data, { headers: getAuthHeader() }),
  addMedication: (id, patientId, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/patients/${patientId}/medications`, data, { headers: getAuthHeader() }),
  // Admissions (ADT)
  admitPatient: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/admissions`, data, { headers: getAuthHeader() }),
  getAdmissions: (id, params) => axios.get(`${API_BASE_URL}/hospitals/${id}/admissions`, { headers: getAuthHeader(), params }),
  getAdmissionById: (id, admId) => axios.get(`${API_BASE_URL}/hospitals/${id}/admissions/${admId}`, { headers: getAuthHeader() }),
  dischargePatient: (id, admId, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/admissions/${admId}/discharge`, data, { headers: getAuthHeader() }),
  transferPatient: (id, admId, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/admissions/${admId}/transfer`, data, { headers: getAuthHeader() }),
  addOrder: (id, admId, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/admissions/${admId}/orders`, data, { headers: getAuthHeader() }),
  addNursingNote: (id, admId, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/admissions/${admId}/notes`, data, { headers: getAuthHeader() }),
  // Quality & HL7
  updateQuality: (id, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/quality`, data, { headers: getAuthHeader() }),
  updateHL7Config: (id, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/hl7-config`, data, { headers: getAuthHeader() }),
  generateHL7Message: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/hl7-message`, data, { headers: getAuthHeader() }),
  // Equipment
  addEquipment: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/equipment`, data, { headers: getAuthHeader() }),
  updateEquipment: (id, equipId, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/equipment/${equipId}`, data, { headers: getAuthHeader() }),
  deleteEquipment: (id, equipId) => axios.delete(`${API_BASE_URL}/hospitals/${id}/equipment/${equipId}`, { headers: getAuthHeader() }),
  // Insurance
  addInsurance: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/insurance`, data, { headers: getAuthHeader() }),
  updateInsurance: (id, contractId, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/insurance/${contractId}`, data, { headers: getAuthHeader() }),
  // Emergency Protocols
  getProtocols: (id) => axios.get(`${API_BASE_URL}/hospitals/${id}/protocols`, { headers: getAuthHeader() }),
  addProtocol: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/protocols`, data, { headers: getAuthHeader() }),
  // Emergency Staff (Doctors, Nurses, Ambulances)
  getEmergencyStaff: (id, params) => axios.get(`${API_BASE_URL}/hospitals/${id}/emergency-staff`, { headers: getAuthHeader(), params }),
  createEmergencyStaff: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/emergency-staff`, data, { headers: getAuthHeader() }),
  updateEmergencyStaff: (id, staffId, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/emergency-staff/${staffId}`, data, { headers: getAuthHeader() }),
  deleteEmergencyStaff: (id, staffId) => axios.delete(`${API_BASE_URL}/hospitals/${id}/emergency-staff/${staffId}`, { headers: getAuthHeader() }),
  getEmergencyDashboard: (id) => axios.get(`${API_BASE_URL}/hospitals/${id}/emergency-dashboard`, { headers: getAuthHeader() }),
  // ═══ Work Orders (General Services, Support, Maintenance) ═══
  createWorkOrder: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/work-orders`, data, { headers: getAuthHeader() }),
  getWorkOrders: (id, params) => axios.get(`${API_BASE_URL}/hospitals/${id}/work-orders`, { headers: getAuthHeader(), params }),
  updateWorkOrder: (id, orderId, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/work-orders/${orderId}`, data, { headers: getAuthHeader() }),
  deleteWorkOrder: (id, orderId) => axios.delete(`${API_BASE_URL}/hospitals/${id}/work-orders/${orderId}`, { headers: getAuthHeader() }),
  // ═══ Pharmacy - Medications ═══
  createMedication: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/medications`, data, { headers: getAuthHeader() }),
  getMedications: (id, params) => axios.get(`${API_BASE_URL}/hospitals/${id}/medications`, { headers: getAuthHeader(), params }),
  updateMedication: (id, medId, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/medications/${medId}`, data, { headers: getAuthHeader() }),
  deleteMedication: (id, medId) => axios.delete(`${API_BASE_URL}/hospitals/${id}/medications/${medId}`, { headers: getAuthHeader() }),
  // ═══ Pharmacy - Prescriptions (including IV Preparations & Consultations) ═══
  createPrescription: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/prescriptions`, data, { headers: getAuthHeader() }),
  getPrescriptions: (id, params) => axios.get(`${API_BASE_URL}/hospitals/${id}/prescriptions`, { headers: getAuthHeader(), params }),
  updatePrescription: (id, rxId, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/prescriptions/${rxId}`, data, { headers: getAuthHeader() }),
  deletePrescription: (id, rxId) => axios.delete(`${API_BASE_URL}/hospitals/${id}/prescriptions/${rxId}`, { headers: getAuthHeader() }),
  // ═══ Sterilization (Loads, Equipment, Quality Tests, Instrument Sets) ═══
  createSterilizationRecord: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/sterilization`, data, { headers: getAuthHeader() }),
  getSterilizationRecords: (id, params) => axios.get(`${API_BASE_URL}/hospitals/${id}/sterilization`, { headers: getAuthHeader(), params }),
  updateSterilizationRecord: (id, recordId, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/sterilization/${recordId}`, data, { headers: getAuthHeader() }),
  deleteSterilizationRecord: (id, recordId) => axios.delete(`${API_BASE_URL}/hospitals/${id}/sterilization/${recordId}`, { headers: getAuthHeader() }),
  // ═══ Admin Records (KPIs, Meetings, Goals, Risks, Contracts, Training, etc.) ═══
  createAdminRecord: (id, data) => axios.post(`${API_BASE_URL}/hospitals/${id}/admin-records`, data, { headers: getAuthHeader() }),
  getAdminRecords: (id, params) => axios.get(`${API_BASE_URL}/hospitals/${id}/admin-records`, { headers: getAuthHeader(), params }),
  updateAdminRecord: (id, recordId, data) => axios.put(`${API_BASE_URL}/hospitals/${id}/admin-records/${recordId}`, data, { headers: getAuthHeader() }),
  deleteAdminRecord: (id, recordId) => axios.delete(`${API_BASE_URL}/hospitals/${id}/admin-records/${recordId}`, { headers: getAuthHeader() }),
};
